import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Settings,
  ChevronRight,
  Mail,
  FileText,
  LogOut,
  Users,
  Wallet as WalletIcon,
  Crown,
  Globe,
  Shield,
  Bell as BellIcon,
  Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { InitialsAvatar } from "@/components/common/InitialsAvatar";
import { Money } from "@/components/common/Money";
import { useAuth, signOut } from "@/hooks/useAuth";
import { useReferralStats } from "@/hooks/useReferral";
import { useEarnings } from "@/hooks/useEarnings";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Profile() {
  const { t, i18n } = useTranslation();
  const { profile, isAdmin } = useAuth();
  const referralStats = useReferralStats();
  const earnings = useEarnings();
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function toggleNotifs(checked: boolean) {
    if (!profile) return;
    const { error } = await supabase
      .from("users")
      .update({ notifs_enabled: checked })
      .eq("id", profile.id);
    if (error) toast.error(error.message);
    else qc.invalidateQueries({ queryKey: ["profile"] });
  }

  async function onSignOut() {
    if (!confirm(t("auth.logoutConfirm"))) return;
    try {
      await signOut();
      navigate("/welcome", { replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  async function changeLanguage(lng: string) {
    await i18n.changeLanguage(lng);
    if (profile) {
      await supabase.from("users").update({ language: lng as "fr" | "en" }).eq("id", profile.id);
      qc.invalidateQueries({ queryKey: ["profile"] });
    }
  }

  return (
    <div className="space-y-4 pt-2 pb-2">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon-sm"><Link to="/"><ArrowLeft className="size-[18px]" /></Link></Button>
        <h1 className="text-base font-semibold tracking-tight flex-1 text-center">Profil</h1>
        <Button asChild variant="ghost" size="icon-sm">
          <Link to="/profile/edit" aria-label="edit"><Settings className="size-[18px]" /></Link>
        </Button>
      </header>

      {/* Identity */}
      <section className="rounded-2xl border border-white/[0.06] bg-card p-5">
        <div className="flex items-center gap-4">
          <InitialsAvatar name={profile?.first_name} seed={profile?.id} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold tracking-tight truncate">{profile?.first_name ?? "—"}</h2>
              {isAdmin && (
                <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 border border-primary/20 px-1.5 h-5 text-[10px] font-semibold text-primary">
                  <Shield className="size-2.5" /> ADMIN
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">@{profile?.referral_code?.toLowerCase()}</p>
            <p className="text-2xs text-muted-foreground truncate mt-0.5">{profile?.email}</p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-3 gap-2">
        <StatTile icon={WalletIcon} label="Wallet" value={<Money amount={profile?.wallet_balance ?? 0} compact />} to="/wallet" />
        <StatTile icon={Users} label="Parrainés" value={referralStats.total} to="/referral" />
        <StatTile icon={Trophy} label="Gains" value={<Money amount={earnings.total} compact />} to="/history" />
      </section>

      {/* Quick access */}
      <section className="rounded-xl border border-white/[0.06] bg-card divide-y divide-white/[0.06]">
        <Row icon={WalletIcon} label="Mon wallet" to="/wallet" />
        <Row icon={Crown} label="Abonnements" to="/plans" />
        <Row icon={Users} label="Parrainage" to="/referral" />
        <Row icon={FileText} label="Historique" to="/history" />
      </section>

      {/* Préférences */}
      <section className="rounded-xl border border-white/[0.06] bg-card divide-y divide-white/[0.06]">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors">
              <Globe className="size-4 text-muted-foreground" />
              <span className="flex-1 text-sm font-medium">Langue</span>
              <span className="text-xs text-muted-foreground">{i18n.language === "en" ? "English" : "Français"}</span>
              <ChevronRight className="size-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => changeLanguage("fr")}>🇫🇷 Français</DropdownMenuItem>
            <DropdownMenuItem onClick={() => changeLanguage("en")}>🇬🇧 English</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex items-center gap-3 px-4 py-3">
          <BellIcon className="size-4 text-muted-foreground" />
          <div className="flex-1">
            <p className="text-sm font-medium">Notifications</p>
            <p className="text-2xs text-muted-foreground">Alertes in-app</p>
          </div>
          <Switch checked={profile?.notifs_enabled ?? true} onCheckedChange={toggleNotifs} />
        </div>
      </section>

      {isAdmin && (
        <section className="rounded-xl border border-white/[0.06] bg-card">
          <Row icon={Shield} label="Panel admin" to="/admin" />
        </section>
      )}

      <section className="rounded-xl border border-white/[0.06] bg-card divide-y divide-white/[0.06]">
        <Row icon={Mail} label="Nous écrire" href={`mailto:${env.VITE_SUPPORT_EMAIL}`} />
        <Row icon={FileText} label="CGU" to="/legal/terms" />
        <Row icon={FileText} label="Confidentialité" to="/legal/privacy" />
        <Row icon={FileText} label="Mentions légales" to="/legal/notice" />
      </section>

      <Button
        variant="ghost"
        className="w-full text-destructive hover:bg-destructive/10"
        onClick={onSignOut}
      >
        <LogOut className="size-4" /> Se déconnecter
      </Button>

      <p className="text-center text-2xs text-muted-foreground pb-2">v0.1.0 · Super-Pari</p>
    </div>
  );
}

function Row({ icon: Icon, label, to, href }: { icon: React.ElementType; label: string; to?: string; href?: string }) {
  const inner = (
    <span className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors">
      <Icon className="size-4 text-muted-foreground" />
      <span className="flex-1 text-sm font-medium">{label}</span>
      <ChevronRight className="size-3.5 text-muted-foreground" />
    </span>
  );
  if (href) return <a href={href}>{inner}</a>;
  if (to) return <Link to={to}>{inner}</Link>;
  return <span>{inner}</span>;
}

function StatTile({ icon: Icon, label, value, to }: { icon: React.ElementType; label: string; value: React.ReactNode; to: string }) {
  return (
    <Link to={to}>
      <div className={cn(
        "rounded-xl border border-white/[0.06] bg-card px-3 py-3 text-center hover:bg-white/[0.02] transition-colors"
      )}>
        <Icon className="mx-auto size-3.5 text-muted-foreground" strokeWidth={1.75} />
        <p className="mt-1.5 text-sm font-semibold num">{value}</p>
        <p className="text-2xs text-muted-foreground mt-0.5">{label}</p>
      </div>
    </Link>
  );
}
